import { yupResolver } from "@hookform/resolvers/yup"
import { useEffect, useState } from "react"
import * as yup from "yup"

import DialogProvider from "@components/dialog_provider"
import TextInput from "@components/text_input"
import { emailRegex } from "@constants/validation"
import { useCustomerPageContext } from "@contexts/customer"
import { customersUpdateUser } from "@domain/customer/infos"
import useControlledForm from "@hooks/use_controlled_form"
import { Stack } from "@mui/material"
import { useQueryClient } from "@tanstack/react-query"
import { showErrorToast } from "@utils/toast"
import { CustomerPerson, CustomerType } from "__generated__/graphql"

export type UserFormData = {
  email: string
  role: string
}

export default function UpdateCustomerUser({
  isOpen,
  setClose,
  selectedRow
}: {
  isOpen: boolean
  selectedRow?: CustomerPerson
  setClose: () => void
}) {
  const { translate, setUsers, users } = useCustomerPageContext()

  const schema = yup
    .object({
      email: yup
        .string()
        .matches(emailRegex, translate("email_error_invalid_email"))
        .email(translate("email_error_invalid_email"))
        .required(translate("email_required")),
      role: yup.string().required(translate("role_required"))
    })
    .required()

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid }
  } = useControlledForm<UserFormData>({
    resolver: yupResolver(schema),
    mode: "all"
  })

  useEffect(() => {
    if (selectedRow) {
      reset({
        email: selectedRow.email,
        role: selectedRow.type
      })
    }
  }, [selectedRow])

  const [loading, setLoading] = useState(false)
  const queryClient = useQueryClient()

  function handleClose() {
    if (loading) {
      return
    }
    setClose()
    reset()
  }

  async function onSubmit(data: UserFormData) {
    if (!selectedRow?._id) {
      return
    }

    setLoading(true)
    const {
      errorMessage,
      isSuccess,
      data: res
    } = await customersUpdateUser({
      _id: selectedRow?._id,
      customerId: selectedRow.customer,
      role: data.role as CustomerType,
      email: data.email
    })
    if (!isSuccess) {
      setLoading(false)
      showErrorToast(String(errorMessage))
      return
    }
    setUsers?.(
      users.map((user) => {
        if (user._id === selectedRow?._id) {
          return {
            ...user,
            type: res?.type as CustomerType,
            email: data.email
          }
        }
        return user
      })
    )
    queryClient.invalidateQueries({ queryKey: ["customer-data-table"] })
    setLoading(false)
    handleClose()
  }

  return (
    <DialogProvider
      title={translate("update_user")}
      leftButton={{
        text: translate("cancel"),
        color: "secondary",
        onClick: setClose,
        disabled: loading
      }}
      rightButton={{
        text: translate("save"),
        color: "primary",
        onClick: handleSubmit(onSubmit),
        loading: loading,
        disabled: !isValid
      }}
      isOpen={isOpen}
      setClose={handleClose}
      size="small"
    >
      <Stack gap={1}>
        <TextInput<UserFormData>
          label={translate("email")}
          name="email"
          placeholder={translate("email")}
          control={control}
          disabled={loading}
        />

        <TextInput
          type="select"
          disabled={loading}
          name="role"
          control={control}
          label={translate("role")}
          placeholder={translate("role")}
          selectOptions={[
            { label: "Admin", value: "ADMIN" },
            { label: "Owner", value: "OWNER" },
            { label: "Personel", value: "PERSONEL" }
          ]}
        />
      </Stack>
    </DialogProvider>
  )
}
